import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutChangeEvent, Pressable, View } from 'react-native'
import QRCode from 'react-native-qrcode-svg'

import { getIsViewOnly } from '@ambire-common/utils/accounts'
import CloseIcon from '@common/assets/svg/CloseIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ShieldCheckFilledIcon from '@common/assets/svg/ShieldCheckFilledIcon'
import AccountAddress from '@common/components/AccountAddress'
import Alert from '@common/components/Alert'
import { isSmartAccount } from '@ambire-common/libs/account/account'
import Avatar from '@common/components/Avatar/Avatar'
import DomainBadge from '@common/components/Avatar/DomainBadge'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import useController from '@common/hooks/useController'
import useNetworks from '@common/hooks/useNetworks'
import useReverseLookup from '@common/hooks/useReverseLookup'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { sortNetworksPopularFirst } from '@common/utils/sortNetworks'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useRailgunContext from '@web/hooks/useRailgunContext'

import getStyles from './styles'

// Vertical space the modal takes around this content: the web modal card is
// capped at `window height - 100` and pads its content 20px top and bottom.
// Content taller than `window height - SHEET_CHROME` scrolls inside the popup.
const SHEET_CHROME = 140
// Must match styles.networks gap and fit a "+NN" label in styles.networkOverflow.
const NETWORK_CHIP_GAP = 8
const OVERFLOW_CHIP_MAX_WIDTH = 48

type Props = {
  onClose: () => void
}

const ReceiveSheetContent: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()
  const { styles, theme } = useTheme(getStyles)

  const {
    state: { account }
  } = useController('SelectedAccountController')
  const {
    state: { keys }
  } = useController('KeystoreController')

  const { mode } = useAppMode()
  const isPrivate = mode === ModeEnum.PRIVATE
  const { currentRailgunWallet } = useRailgunContext()

  const accent = (isPrivate ? theme.privateAccent : theme.primary) as string

  const address = useMemo(() => {
    if (isPrivate && currentRailgunWallet?.railgunAddress) return currentRailgunWallet.railgunAddress
    return account?.addr || ''
  }, [isPrivate, currentRailgunWallet?.railgunAddress, account?.addr])

  const { isLoading: isDomainResolving, ens } = useReverseLookup({ address })
  const isViewOnly = useMemo(
    () => !account?.safeCreation && getIsViewOnly(keys, account?.associatedKeys || []),
    [account, keys]
  )

  const { label, pfp } = account?.preferences || { label: '', pfp: '' }

  const qrCodeRef: any = useRef(null)
  const [qrCodeError, setQrCodeError] = useState<string | boolean | null>(null)

  const allNetworks = useNetworks({ acc: account })
  const accountNetworks = useMemo(
    () =>
      sortNetworksPopularFirst(
        allNetworks.filter((n) => {
          if (n.isNotSupported) return false
          // Private mode only supports the Railgun chains (Ethereum, Arbitrum, BNB)
          if (isPrivate) return EXTENSION_PRIVATE_CHAIN_IDS.includes(Number(n.chainId))
          return true
        })
      ),
    [allNetworks, isPrivate]
  )

  // Network chips (logo + name) wrap onto as many rows as needed, which on a
  // short screen pushes the popup's bottom out of view. When that happens, show
  // only the chips that fit and cap the list with a "+N" chip (same idea as the
  // Shield screen's ChainStack); pressing it reveals the rest.
  //
  // The full list is measured once per layout key (width changes how chips wrap;
  // mode/view-only change what renders): the content height, the chip area width
  // and each chip's box. Removing trailing chips never re-wraps the earlier rows,
  // so the visible count can be derived from those numbers for any window height.
  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const availableHeight = windowHeight - SHEET_CHROME
  const layoutKey = `${windowWidth}|${accountNetworks.length}|${isViewOnly}`
  const [visibleNetworkCount, setVisibleNetworkCount] = useState<number | null>(null)
  const [isNetworksExpanded, setIsNetworksExpanded] = useState(false)
  const measureRef = useRef<{
    key: string
    contentHeight: number
    areaWidth: number
    chips: { x: number; y: number; width: number; height: number }[]
  }>({ key: '', contentHeight: 0, areaWidth: 0, chips: [] })
  const isShowingAllNetworks = visibleNetworkCount === null || isNetworksExpanded

  const recomputeVisibleNetworks = useCallback(() => {
    const { key, contentHeight, areaWidth, chips } = measureRef.current
    const total = accountNetworks.length
    if (key !== layoutKey || !contentHeight || !areaWidth) return
    if (chips.filter(Boolean).length < total) return

    const overflow = contentHeight - availableHeight
    if (overflow <= 0) {
      setVisibleNetworkCount(null)
      return
    }
    const lastChip = chips[total - 1]!
    const allowedBottom = lastChip.y + lastChip.height - overflow
    let visible = chips.filter((c) => c.y + c.height <= allowedBottom).length
    // The "+N" chip goes at the end of the last visible row: keep dropping chips
    // until that row has room for it.
    while (visible > 1) {
      const rowY = chips[visible - 1]!.y
      const row = chips.slice(0, visible).filter((c) => c.y === rowY)
      const rowWidth =
        row.reduce((sum, c) => sum + c.width, 0) + NETWORK_CHIP_GAP * (row.length - 1)
      if (rowWidth + NETWORK_CHIP_GAP + OVERFLOW_CHIP_MAX_WIDTH <= areaWidth) break
      visible -= 1
    }
    visible = Math.max(1, visible)
    setVisibleNetworkCount(visible >= total ? null : visible)
  }, [accountNetworks.length, layoutKey, availableHeight])

  useEffect(() => {
    if (measureRef.current.key !== layoutKey) {
      measureRef.current = { key: layoutKey, contentHeight: 0, areaWidth: 0, chips: [] }
      setVisibleNetworkCount(null)
      setIsNetworksExpanded(false)
      return
    }
    recomputeVisibleNetworks()
  }, [layoutKey, recomputeVisibleNetworks])

  const onContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      // Only the full list is a valid measurement.
      if (!isShowingAllNetworks || measureRef.current.key !== layoutKey) return
      measureRef.current.contentHeight = e.nativeEvent.layout.height
      if (!isNetworksExpanded) recomputeVisibleNetworks()
    },
    [isShowingAllNetworks, isNetworksExpanded, layoutKey, recomputeVisibleNetworks]
  )

  const onChipLayout = useCallback(
    (index: number, e: LayoutChangeEvent) => {
      if (!isShowingAllNetworks || measureRef.current.key !== layoutKey) return
      const { x, y, width, height } = e.nativeEvent.layout
      measureRef.current.chips[index] = { x, y, width, height }
      if (!isNetworksExpanded) recomputeVisibleNetworks()
    },
    [isShowingAllNetworks, isNetworksExpanded, layoutKey, recomputeVisibleNetworks]
  )

  const onChipAreaLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (measureRef.current.key !== layoutKey) return
      measureRef.current.areaWidth = e.nativeEvent.layout.width
      if (!isNetworksExpanded) recomputeVisibleNetworks()
    },
    [isNetworksExpanded, layoutKey, recomputeVisibleNetworks]
  )

  const shownNetworks = isShowingAllNetworks
    ? accountNetworks
    : accountNetworks.slice(0, visibleNetworkCount as number)
  const hiddenNetworks = accountNetworks.slice(shownNetworks.length)

  return (
    <View style={spacings.phTy} onLayout={onContentLayout}>
      {/* Header — centered title + mode icon, close button top-right */}
      <View style={styles.header}>
        <View style={[flexbox.directionRow, flexbox.alignCenter]}>
          {isPrivate ? (
            <ShieldCheckFilledIcon width={20} height={20} color={accent} />
          ) : (
            <GlobeIcon width={20} height={20} color={accent} />
          )}
          <Text weight="medium" fontSize={18} style={spacings.plTy}>
            {isPrivate ? t('Private receive') : t('Public receive')}
          </Text>
        </View>
        <Pressable style={styles.closeButton} onPress={onClose} hitSlop={8}>
          <CloseIcon width={14} height={14} color={theme.secondaryText as string} />
        </Pressable>
      </View>

      {/* Account avatar + label */}
      <View style={[spacings.mtLg, flexbox.alignCenter]}>
        <Avatar
          size={48}
          pfp={pfp}
          address={account?.addr || ''}
          isSmart={isSmartAccount(account)}
          style={spacings.pr0}
        />
        <Text weight="semiBold" fontSize={16} style={spacings.mtTy}>
          {label}
        </Text>
      </View>

      {/* QR code */}
      <View style={[spacings.mtLg, flexbox.alignCenter]}>
        {!!account && !qrCodeError && (
          <View style={styles.qrBox}>
            <QRCode
              value={address}
              size={180}
              quietZone={6}
              getRef={qrCodeRef}
              onError={() => setQrCodeError(t('Failed to load QR code!') as string)}
            />
          </View>
        )}
        {!!qrCodeError && (
          <Text appearance="errorText" weight="medium">
            {t('Failed to display QR code.')}
          </Text>
        )}
      </View>

      {/* Address pill with copy */}
      <View style={[spacings.mtLg, styles.addressPill]}>
        {!isPrivate && <DomainBadge ens={ens} />}
        <AccountAddress
          isLoading={isDomainResolving}
          ens={!isPrivate ? ens : undefined}
          address={address}
          plainAddressMaxLength={24}
          fontSize={14}
        />
      </View>

      {!!isViewOnly && (
        <View style={[spacings.mtMd, flexbox.alignCenter]}>
          <Alert size="sm" type="warning" title={t('Selected account is view only.')} />
        </View>
      )}

      {/* Supported networks */}
      {accountNetworks.length > 0 && (
        <View style={spacings.mtLg}>
          <Text
            weight="regular"
            appearance="tertiaryText"
            fontSize={13}
            style={[spacings.mbSm, { textAlign: 'center' }]}
          >
            {t('Supported networks')}
          </Text>
          <View style={styles.networks} onLayout={onChipAreaLayout}>
            {shownNetworks.map(({ chainId, name }: any, index: number) => (
              <View
                key={chainId.toString()}
                style={styles.network}
                onLayout={(e) => onChipLayout(index, e)}
              >
                <NetworkIcon id={chainId.toString()} size={20} scale={1} />
                <Text weight="medium" fontSize={13} appearance="primaryText" numberOfLines={1}>
                  {name}
                </Text>
              </View>
            ))}
            {hiddenNetworks.length > 0 && (
              <Pressable
                style={styles.networkOverflow}
                onPress={() => setIsNetworksExpanded(true)}
                accessibilityRole="button"
                accessibilityLabel={t('Show {{count}} more networks', {
                  count: hiddenNetworks.length
                })}
                // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
                dataSet={createGlobalTooltipDataSet({
                  id: 'receive-networks-overflow',
                  content: hiddenNetworks.map((n) => n.name).join(', ')
                })}
              >
                <Text weight="semiBold" fontSize={13} appearance="primaryText">
                  {`+${hiddenNetworks.length}`}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      )}
    </View>
  )
}

export default React.memo(ReceiveSheetContent)
